import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Modal,
  Typography,
} from "@mui/material";
import XLSX from "xlsx-js-style";
import { planCRUD } from "../../../http";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const monthNames = [
  "январ",
  "феврал",
  "март",
  "апрел",
  "май",
  "июн",
  "июл",
  "август",
  "сентябр",
  "октябр",
  "ноябр",
  "декабр",
];

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const parseExcelDate = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "number") {
    const parsed = XLSX.SSF?.parse_date_code?.(value);
    if (parsed?.y && parsed?.m && parsed?.d) {
      return new Date(parsed.y, parsed.m - 1, parsed.d);
    }
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const match = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (match) {
      const date = new Date(
        Number(match[3]),
        Number(match[2]) - 1,
        Number(match[1])
      );
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
};

const toNumberOrZero = (value) => {
  const number = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(number) ? number : 0;
};

const expandMergedCells = (aoa, merges = []) => {
  const out = aoa.map((row) => [...(row || [])]);
  (merges || []).forEach((merge) => {
    const startRow = merge?.s?.r;
    const endRow = merge?.e?.r;
    const startCol = merge?.s?.c;
    const endCol = merge?.e?.c;
    if (
      startRow == null ||
      endRow == null ||
      startCol == null ||
      endCol == null
    ) {
      return;
    }

    const value = out[startRow]?.[startCol];
    if (value == null || value === "") return;

    for (let r = startRow; r <= endRow; r++) {
      if (!out[r]) out[r] = [];
      for (let c = startCol; c <= endCol; c++) {
        if (out[r][c] == null || out[r][c] === "") {
          out[r][c] = value;
        }
      }
    }
  });
  return out;
};

const resolveMonthColumns = (headerRow) => {
  const map = {};
  (headerRow || []).forEach((cell, idx) => {
    const normalized = normalize(cell);
    if (!normalized) return;
    monthNames.forEach((name, monthIdx) => {
      if (normalized.includes(name) && map[monthIdx + 1] == null) {
        map[monthIdx + 1] = idx;
      }
    });
  });
  return map;
};

const findHeaderRowIndex = (aoa) => {
  for (let i = 0; i < Math.min(40, aoa.length); i++) {
    const row = aoa[i] || [];
    const hasDistrict = row.some((cell) =>
      normalize(cell).includes("район/город")
    );
    const hasSubject = row.some((cell) =>
      normalize(cell).includes("наименование проверяемого субъекта")
    );
    if (hasDistrict && hasSubject) {
      return i;
    }
  }
  return -1;
};

const findColumn = (row, tokens) => {
  for (let i = 0; i < (row || []).length; i++) {
    const text = normalize(row[i]);
    if (!text) continue;
    if (tokens.every((token) => text.includes(token))) return i;
  }
  return -1;
};

const resolveBaseColumns = (headerRow) => {
  const map = {
    number: findColumn(headerRow, ["№"]),
    districtCity: findColumn(headerRow, ["район/город"]),
    inspectedEntityName: findColumn(headerRow, [
      "наименование",
      "проверяемого",
      "субъекта",
    ]),
    individualOrBusinessId: findColumn(headerRow, ["идентификационный"]),
    inspectedObjectName: findColumn(headerRow, ["наименование", "объекта"]),
    location: findColumn(headerRow, ["местонахождение"]),
    riskGroup: findColumn(headerRow, ["группа", "риска"]),
    entrepreneurshipCategory: findColumn(headerRow, [
      "категория",
      "предпринимательства",
    ]),
    primaryRegistrationDate: findColumn(headerRow, ["дата", "первичной"]),
    inspectedObjectNameAlt: findColumn(headerRow, [
      "если не указан",
      "столбце 5",
    ]),
  };
  return map;
};

const resolveIndexGroups = (headerRow, level2Row) => {
  const groups = [];
  const maxLen = Math.max(headerRow?.length || 0, level2Row?.length || 0);

  for (let c = 0; c < maxLen - 5; c++) {
    const l2c = normalize(level2Row?.[c]);
    const l2c1 = normalize(level2Row?.[c + 1]);
    const l2c2 = normalize(level2Row?.[c + 2]);
    const l2c3 = normalize(level2Row?.[c + 3]);
    const l2c4 = normalize(level2Row?.[c + 4]);
    const l2c5 = normalize(level2Row?.[c + 5]);

    const isMetricBlock =
      l2c === "план" &&
      l2c1.includes("исслед") &&
      l2c2.includes("сум") &&
      l2c3 === "план" &&
      l2c4.includes("исслед") &&
      l2c5.includes("сум");

    if (!isMetricBlock) continue;

    const name = String(headerRow?.[c] || "").trim() || `Индекс ${c + 1}`;
    groups.push({
      name,
      startCol: c,
      plannedPlanCol: c,
      plannedResearchCol: c + 1,
      plannedSumCol: c + 2,
      donePlanCol: c + 3,
      doneResearchCol: c + 4,
      doneSumCol: c + 5,
    });
    c += 5;
  }

  return groups;
};

const getString = (row, col) => String((col >= 0 ? row?.[col] : "") || "").trim();

const isDataRow = (row, cols) => {
  const rowNum = Number(row?.[cols.number]);
  if (!Number.isFinite(rowNum)) return false;
  const district = getString(row, cols.districtCity);
  const subject = getString(row, cols.inspectedEntityName);
  return Boolean(district || subject);
};

export default function PlanImportExcelModal({
  open,
  onClose,
  onDone,
  planDocumentId,
}) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [parseMeta, setParseMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [uploadErrors, setUploadErrors] = useState([]);

  const preview = useMemo(() => rows.slice(0, 8), [rows]);

  const reset = () => {
    setFileName("");
    setRows([]);
    setError("");
    setParseMeta(null);
    setUploading(false);
    setProgress({ done: 0, total: 0 });
    setUploadErrors([]);
  };

  const close = () => {
    reset();
    onClose && onClose();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setError("");
      setFileName(file.name);

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawAoa = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: true,
        defval: "",
      });
      const aoa = expandMergedCells(rawAoa, worksheet["!merges"] || []);

      const headerRowIndex = findHeaderRowIndex(aoa);
      if (headerRowIndex < 0) {
        throw new Error("Не удалось найти строку заголовка таблицы.");
      }

      const headerRow = aoa[headerRowIndex] || [];
      const level2Row = aoa[headerRowIndex + 2] || [];
      const baseCols = resolveBaseColumns(headerRow);
      const monthCols = resolveMonthColumns(headerRow);
      const indexGroups = resolveIndexGroups(headerRow, level2Row);

      if (baseCols.inspectedEntityName < 0 || baseCols.districtCity < 0) {
        throw new Error("Не распознаны базовые колонки (район/субъект).");
      }

      const parsed = [];

      for (let i = headerRowIndex + 3; i < aoa.length; i++) {
        const row = aoa[i];
        if (!isDataRow(row, baseCols)) continue;

        const item = {
          __row: i + 1,
          districtCity: getString(row, baseCols.districtCity),
          inspectedEntityName: getString(row, baseCols.inspectedEntityName),
          individualOrBusinessId: getString(row, baseCols.individualOrBusinessId),
          inspectedObjectName: getString(row, baseCols.inspectedObjectName),
          location: getString(row, baseCols.location),
          riskGroup: getString(row, baseCols.riskGroup),
          entrepreneurshipCategory: getString(row, baseCols.entrepreneurshipCategory),
          primaryRegistrationDate: parseExcelDate(row?.[baseCols.primaryRegistrationDate]),
          inspectedObjectNameAlt: getString(row, baseCols.inspectedObjectNameAlt),
          months: {},
        };

        for (let m = 1; m <= 12; m++) {
          const col = monthCols[m];
          item.months[m] = col != null ? toNumberOrZero(row?.[col]) : 0;
        }

        parsed.push(item);
      }

      setRows(parsed);
      setUploadErrors([]);
      setParseMeta({
        headerRow: headerRowIndex + 1,
        monthColsFound: Object.keys(monthCols).length,
        indexGroupsFound: indexGroups.length,
        firstIndexGroups: indexGroups.slice(0, 6).map((g) => g.name),
      });
    } catch (e) {
      setRows([]);
      setError(String(e));
      setParseMeta(null);
    }
  };

  const upload = async () => {
    if (!rows.length) {
      alert("Нет распознанных строк для загрузки.");
      return;
    }
    if (planDocumentId == null || !Number.isFinite(Number(planDocumentId))) {
      alert("Не указан документ плана (planDocumentId).");
      return;
    }

    const nowIso = new Date().toISOString();
    const importYear = new Date().getFullYear();
    const errors = [];
    let successCount = 0;

    setUploading(true);
    setUploadErrors([]);
    setProgress({ done: 0, total: rows.length });

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        const monthValues = row.months || {};
        const monthTotal = Object.values(monthValues).reduce(
          (acc, val) => acc + toNumberOrZero(val),
          0
        );

        const planPayload = {
          planDocumentId: Number(planDocumentId),
          createdAt: nowIso,
          month: new Date(importYear, 0, 1).toISOString(),
          amount: monthTotal,
          sum: monthTotal,
          price: 0,
          costCoefficient: 0,
          isDeleted: false,
          districtCity: row.districtCity || null,
          inspectedEntityName: row.inspectedEntityName || null,
          individualOrBusinessId: row.individualOrBusinessId || null,
          inspectedObjectName: row.inspectedObjectName || null,
          location: row.location || null,
          riskGroup: row.riskGroup || null,
          entrepreneurshipCategory: row.entrepreneurshipCategory || null,
          primaryRegistrationDate: row.primaryRegistrationDate
            ? new Date(row.primaryRegistrationDate).toISOString()
            : nowIso,
          inspectedObjectNameAlt: row.inspectedObjectNameAlt || null,
          month1: toNumberOrZero(monthValues[1]),
          month2: toNumberOrZero(monthValues[2]),
          month3: toNumberOrZero(monthValues[3]),
          month4: toNumberOrZero(monthValues[4]),
          month5: toNumberOrZero(monthValues[5]),
          month6: toNumberOrZero(monthValues[6]),
          month7: toNumberOrZero(monthValues[7]),
          month8: toNumberOrZero(monthValues[8]),
          month9: toNumberOrZero(monthValues[9]),
          month10: toNumberOrZero(monthValues[10]),
          month11: toNumberOrZero(monthValues[11]),
          month12: toNumberOrZero(monthValues[12]),
        };

        const createdPlan = await planCRUD.create(planPayload);
        const planId = createdPlan?.id || createdPlan?.Id;
        if (!planId) {
          throw new Error("Не удалось получить id созданного плана.");
        }

        successCount += 1;
      } catch (e) {
        errors.push({
          row: row.__row,
          message: String(e),
        });
      } finally {
        setProgress((p) => ({ ...p, done: i + 1 }));
      }
    }

    setUploading(false);
    setUploadErrors(errors);
    if (errors.length) {
      alert(`Импорт завершен: успешно ${successCount}, ошибок ${errors.length}.`);
    } else {
      alert(`Импорт успешно завершен: ${successCount} строк.`);
      onDone && onDone({ successCount, errors: 0 });
    }
  };

  return (
    <Modal open={open} onClose={close}>
      <Box sx={style}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Импорт Excel — объекты плана
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button variant="outlined" component="label">
              Выбрать Excel
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFile}
              />
            </Button>
            {fileName ? (
              <Typography sx={{ ml: 2, display: "inline-block" }}>
                {fileName}
              </Typography>
            ) : null}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Строки создаются в документе плана #{String(planDocumentId)}.
              Импорт загружает поля объекта (без индикаторов) — показатели
              добавляются в карточке объекта.
            </Typography>
          </Grid>

          {error ? (
            <Grid item xs={12}>
              <Typography color="error">{error}</Typography>
            </Grid>
          ) : null}

          {parseMeta ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Распознано: заголовок строка {parseMeta.headerRow}, месяцев{" "}
                {parseMeta.monthColsFound}/12, групп индексов{" "}
                {parseMeta.indexGroupsFound}
              </Typography>
              {parseMeta.firstIndexGroups?.length ? (
                <Typography variant="body2" color="text.secondary">
                  Первые группы: {parseMeta.firstIndexGroups.join(" | ")}
                </Typography>
              ) : null}
            </Grid>
          ) : null}

          {rows.length ? (
            <Grid item xs={12}>
              <Typography sx={{ mb: 1 }}>
                Найдено строк: <b>{rows.length}</b> (показано первые 8)
              </Typography>
              <Box
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  p: 2,
                  fontFamily: "monospace",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                }}
              >
                {preview
                  .map(
                    (row) =>
                      `#${row.__row} ${row.districtCity} | ${row.inspectedEntityName}`
                  )
                  .join("\n")}
              </Box>
            </Grid>
          ) : null}

          {uploading ? (
            <Grid item xs={12}>
              <Typography sx={{ mb: 1 }}>
                Загрузка: {progress.done}/{progress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress.total ? (progress.done / progress.total) * 100 : 0}
              />
            </Grid>
          ) : null}

          {uploadErrors.length ? (
            <Grid item xs={12}>
              <Typography color="error" sx={{ mb: 1 }}>
                Ошибки: {uploadErrors.length}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #f2c2c2",
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 200,
                  overflowY: "auto",
                  fontFamily: "monospace",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                }}
              >
                {uploadErrors
                  .map((e) => `Строка ${e.row}: ${e.message}`)
                  .join("\n")}
              </Box>
            </Grid>
          ) : null}

          <Grid item xs={12} sx={{ display: "flex", gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              onClick={upload}
              disabled={uploading || !rows.length}
            >
              Загрузить в систему
            </Button>
            <Button variant="outlined" onClick={close}>
              Закрыть
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

